/**
 * Phase 5 — Customer Quotation Inbox.
 *
 * Lists quotations the member has received with view/accept/decline actions.
 * Marks a quotation viewed automatically when expanded.
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, CheckCircle2, XCircle, Eye, ExternalLink,
  Loader2, ChevronDown, ChevronUp, IndianRupee
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending: { label: 'Awaiting Your Review', tone: 'text-amber-300 bg-amber-500/10 ring-amber-500/30' },
  viewed: { label: 'Reviewed', tone: 'text-blue-300 bg-blue-500/10 ring-blue-500/30' },
  accepted: { label: 'Accepted', tone: 'text-emerald-300 bg-emerald-500/10 ring-emerald-500/30' },
  declined: { label: 'Declined', tone: 'text-red-300 bg-red-500/10 ring-red-500/30' },
  expired: { label: 'Expired', tone: 'text-text-secondary bg-white/5 ring-white/10' },
};

const fmt = (amount, currency) => {
  if (amount == null) return '—';
  try {
    return new Intl.NumberFormat('en', {
      style: 'currency', currency: currency || 'AED', maximumFractionDigits: 0
    }).format(amount);
  } catch {
    return `${currency || ''} ${Number(amount).toLocaleString()}`;
  }
};

const QuotationRow = ({ q, onChange }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [declineNote, setDeclineNote] = useState('');
  const meta = STATUS_META[q.status] || STATUS_META.pending;

  const handleExpand = async () => {
    if (!open && q.status === 'pending') {
      try { await axios.post(`${API}/quotations/${q.id}/view`); onChange(); } catch {/* ignore */}
    }
    setOpen((v) => !v);
  };

  const respond = async (decision) => {
    setBusy(true);
    try {
      await axios.post(`${API}/quotations/${q.id}/respond`, {
        decision,
        note: decision === 'declined' ? declineNote.trim() : '',
      });
      toast.success(decision === 'accepted' ? 'Quotation accepted' : 'Quotation declined');
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const isTerminal = q.status === 'accepted' || q.status === 'declined' || q.status === 'expired';

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid={`quotation-row-${q.id}`}>
      <button
        onClick={handleExpand}
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-white/5 transition-colors"
      >
        <div className="w-10 h-10 rounded-lg bg-gold/10 ring-1 ring-gold/20 flex items-center justify-center flex-shrink-0">
          <FileText className="w-4 h-4 text-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="font-semibold text-white truncate">{q.title || 'Quotation'}</span>
            <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-semibold rounded-full ring-1 ${meta.tone}`}>
              {meta.label}
            </span>
          </div>
          <div className="text-xs text-text-secondary">
            from <span className="text-white/70">{q.partner_name}</span>
            <span className="mx-1.5">·</span>
            <span className="text-gold font-semibold">{fmt(q.amount, q.currency)}</span>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-text-secondary" /> : <ChevronDown className="w-4 h-4 text-text-secondary" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="p-4 border-t border-white/5 space-y-3">
              {q.terms && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-text-secondary mb-1">Terms</div>
                  <p className="text-sm text-white/80 leading-relaxed whitespace-pre-line">{q.terms}</p>
                </div>
              )}
              {q.valid_until && (
                <div className="text-xs text-text-secondary">
                  Valid until <span className="text-white/80">{q.valid_until}</span>
                </div>
              )}
              {q.file_url && (
                <a
                  href={q.file_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-xs text-gold hover:text-gold/80 uppercase tracking-[0.18em]"
                  data-testid={`quotation-open-file-${q.id}`}
                >
                  Open document <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}

              {!isTerminal && (
                <div className="pt-3 border-t border-white/5 space-y-3">
                  <Textarea
                    value={declineNote}
                    onChange={(e) => setDeclineNote(e.target.value)}
                    placeholder="Optional note (used only if you decline)…"
                    className="bg-white/5 border-white/10 min-h-[60px] text-sm"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={() => respond('accepted')}
                      disabled={busy}
                      className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-40"
                      data-testid={`quotation-accept-${q.id}`}
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                      Accept
                    </Button>
                    <Button
                      onClick={() => respond('declined')}
                      disabled={busy}
                      variant="outline"
                      className="flex-1 border-white/10 hover:bg-white/5 disabled:opacity-40"
                      data-testid={`quotation-decline-${q.id}`}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Decline
                    </Button>
                  </div>
                </div>
              )}

              <div className="text-[11px] text-text-secondary">
                Quotation issued by <strong className="text-white/70">{q.partner_name}</strong>.
                ASTIIV BASGAT stores and tracks it on your behalf; commercial terms and contracts remain with the verified partner.
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const QuotationsInbox = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/quotations/me`);
      setItems(data.quotations || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  if (loading) {
    return (
      <div className="glass-card p-6 flex items-center gap-3 text-text-secondary">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your quotations…
      </div>
    );
  }
  if (!items.length) return null;

  return (
    <div className="glass-card p-6" data-testid="quotations-inbox">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-gold" />
          <h3 className="font-playfair text-xl font-semibold">Your Quotations</h3>
        </div>
        <span className="text-[10px] uppercase tracking-[0.2em] text-text-secondary">
          {items.length} {items.length === 1 ? 'quotation' : 'quotations'}
        </span>
      </div>
      <div className="space-y-3">
        {items.map((q) => <QuotationRow key={q.id} q={q} onChange={refresh} />)}
      </div>
    </div>
  );
};

export default QuotationsInbox;
